import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  listVitalsHistory,
  type VitalsHistoryEntry,
} from '@/features/timeline/lib/timelineQueries';
import {
  MetricSection,
  type VitalReading,
} from '@/features/timeline/components/metric-section';
import type { VitalSigns } from '@/types/db';

interface MetricDef {
  key: string;
  label: string;
  unit: string;
  extract: (vs: VitalSigns) => number | undefined;
  formatDisplay: (vs: VitalSigns) => string;
  yAxisLabel?: string;
  icon: JSX.Element;
}

const METRICS: MetricDef[] = [
  {
    key: 'hr',
    label: 'Heart Rate',
    unit: 'bpm',
    extract: (vs) => vs.heart_rate,
    formatDisplay: (vs) => String(vs.heart_rate ?? ''),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0" aria-hidden="true">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
  },
  {
    key: 'bp',
    label: 'Blood Pressure',
    unit: 'mmHg',
    extract: (vs) => vs.systolic_bp,
    formatDisplay: (vs) =>
      vs.systolic_bp != null && vs.diastolic_bp != null
        ? `${vs.systolic_bp}/${vs.diastolic_bp}`
        : String(vs.systolic_bp ?? ''),
    yAxisLabel: 'systolic',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0" aria-hidden="true">
        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
      </svg>
    ),
  },
  {
    key: 'sugar',
    label: 'Blood Sugar',
    unit: 'mg/dL',
    extract: (vs) => vs.blood_sugar,
    formatDisplay: (vs) => String(vs.blood_sugar ?? ''),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0" aria-hidden="true">
        <path d="M12 2C8 8 5 11 5 15a7 7 0 0 0 14 0C19 11 16 8 12 2z" />
      </svg>
    ),
  },
  {
    key: 'weight',
    label: 'Weight',
    unit: 'kg',
    extract: (vs) => vs.weight_kg,
    formatDisplay: (vs) => String(vs.weight_kg ?? ''),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0" aria-hidden="true">
        <path d="M5 17a7 7 0 0 1 14 0" />
        <path d="M12 10v7" />
        <circle cx="12" cy="10" r="1.5" />
        <line x1="3" y1="21" x2="21" y2="21" />
      </svg>
    ),
  },
];

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; entries: VitalsHistoryEntry[] }
  | { kind: 'error'; message: string };

function buildReadings(
  entries: VitalsHistoryEntry[],
  metric: MetricDef,
): VitalReading[] {
  return entries
    .map((e) => {
      const val = metric.extract(e.vital_signs);
      if (val == null) return null;
      return {
        id: e.id,
        date: e.event_date,
        value: val,
        displayValue: metric.formatDisplay(e.vital_signs),
      };
    })
    .filter((r): r is VitalReading => r !== null);
}

export function VitalsPage() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setState({ kind: 'loading' });
      const entries = await listVitalsHistory(id);
      setState({ kind: 'ready', entries });
    } catch (err) {
      console.error('[vitals-page] load failed', err);
      setState({ kind: 'error', message: "Couldn't load vitals." });
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const hasAnyVitals =
    state.kind === 'ready' && state.entries.length > 0;

  return (
    <main className="min-h-screen bg-canvas safe-top pb-28">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 bg-surface/95 backdrop-blur border-b border-border">
        <div className="max-w-md mx-auto px-4 h-[56px] flex items-center gap-2">
          <Link
            to={`/profile/${id}`}
            aria-label="Back to profile"
            className="h-10 w-10 -ml-1.5 rounded-full text-ink-muted hover:text-ink active:bg-canvas flex items-center justify-center shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-base text-ink leading-tight">Vitals</h1>
          </div>
        </div>
      </header>

      <div className="max-w-md mx-auto pt-4">
        {/* Loading */}
        {state.kind === 'loading' && (
          <>
            <div className="h-[260px] animate-pulse bg-surface rounded-[20px] mx-4 mb-4 border border-border" />
            <div className="h-[260px] animate-pulse bg-surface rounded-[20px] mx-4 mb-4 border border-border" />
          </>
        )}

        {/* Error */}
        {state.kind === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-[20px] mx-4 mt-2 p-4">
            <p className="text-sm font-semibold text-red-700 mb-2">{state.message}</p>
            <button
              type="button"
              onClick={load}
              className="text-sm font-semibold text-accent"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty — no vitals at all */}
        {state.kind === 'ready' && !hasAnyVitals && (
          <div className="flex flex-col items-center justify-center px-8 py-16 gap-4 text-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-14 w-14 text-evt-vitals-ink opacity-40" aria-hidden="true">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
            <div>
              <p className="text-base font-semibold text-ink mb-1">No vitals recorded yet</p>
              <p className="text-sm text-ink-muted">Add a reading to start tracking trends.</p>
            </div>
            <Link
              to={`/profile/${id}/manual?type=vitals`}
              className="mt-2 inline-flex items-center justify-center h-[44px] px-6 bg-accent text-white rounded-full text-sm font-semibold"
            >
              + Add first vital reading
            </Link>
          </div>
        )}

        {/* Metric sections */}
        {state.kind === 'ready' && hasAnyVitals && (
          <>
            {METRICS.map((metric) => (
              <MetricSection
                key={metric.key}
                metricKey={metric.key}
                label={metric.label}
                unit={metric.unit}
                readings={buildReadings(state.entries, metric)}
                icon={metric.icon}
                yAxisLabel={metric.yAxisLabel}
              />
            ))}
          </>
        )}
      </div>

      {/* Sticky CTA */}
      <div
        className="sticky bottom-0 bg-canvas/90 backdrop-blur-sm border-t border-border"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="max-w-md mx-auto px-4 py-3">
          <Link
            to={`/profile/${id}/manual?type=vitals`}
            className="flex items-center justify-center w-full h-[44px] bg-accent text-white rounded-full text-sm font-semibold"
          >
            + Add vital reading
          </Link>
        </div>
      </div>
    </main>
  );
}
